import { DatePicker } from "@/app/global-components/date-picker";
import { ArrowDown, ChevronUp, Download, Filter } from "lucide-react";
import {
  Chip,
  Menu,
  Checkbox,
  FormControlLabel,
  Typography,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Avatar,
  Card,
  CardContent,
  Grid,
  Container,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocale } from "@/middleware";
import { useTranslate } from "@/locales";
import { format } from "date-fns";
import { arSA, enUS } from "date-fns/locale";
import { showToast } from "@/app/global-components/toaster";
import useItems from "@/hooks/use-item";
import { useSales } from "@/hooks/use-sales";
import TableComponent from "@/app/global-components/table";

enum STATUS_ORDER {
  COMPLETED = "COMPLETED",
  PENDING = "PENDING",
  CANCELLED = "CANCELLED",
  ACCEPTED = "PROCESSING",
  REJECTED = "REJECTED",
}

interface Sale {
  id: string;
  createdAt: string;
  salesNumber: string;
  price: number;
  status: STATUS_ORDER;
  productImage: string;
  productName: string;
  customerAddress: string;
  productId: string;
  orderId: string;
  quantity: number;
  customerName: string;
  notes?: string;
}

const allStatuses = [
  "Delivered",
  "Pending",
  "PROCESSING",
  "Returned",
  "Accepted",
];

function SellerSales() {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);

  const { getSales, updateStatus } = useSales();
  const [sales, setSales] = useState<Sale[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Sale | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const fetchSales = async () => {
    const response = await getSales(10, 1);
    if (response?.data) {
      setSales(response.data);
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusToggle = (status: string) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleClearFilters = () => {
    setSelectedStatuses([]);
  };

  const handleApplyFilters = () => {
    // Here you can implement the actual filtering logic
    // For now, we'll just close the menu
    handleClose();
  };

  const handleRowClick = (order: Sale) => {
    setSelectedOrder(order);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedOrder(null);
  };

  const { getPublishedItem } = useItems();

  useEffect(() => {
    const fetchProduct = async () => {
      if (selectedOrder?.productId) {
        const response = await getPublishedItem(selectedOrder.productId);
        if (response) {
          setSelectedOrder({
            ...selectedOrder,
            productName: response.name,
            productImage: response.images[0],
          });
        }
      }
    };
    fetchProduct();
  }, [selectedOrder]);

  const handleUpdateStatus = async (newStatus: string) => {
    if (!selectedOrder) return;
    setIsUpdating(true);
    try {
      const response = await updateStatus(selectedOrder.id, {
        status: newStatus,
      });
      if (response?.isSuccess) {
        showToast(t("Alert.OrderStatusUpdated"), "success");
        await fetchSales();
        handleCloseDialog();
      } else {
        showToast(t("Alert.OrderTryAgain"), "error");
      }
    } catch (error) {
      showToast(t("Alert.OrderTryAgain"), "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "accepted":
        return { bg: "#DDF7E3", color: "#164B60" };
      case "delivered":
        return { bg: "#DDF7E3", color: "#164B60" };
      case "returned":
        return { bg: "#FFE5E5", color: "#FF4B4B" };
      case "processing":
        return { bg: "#FFF4E5", color: "#FF8C00" };
      case "pending":
        return { bg: "#F0F0F0", color: "#666666" };
      default:
        return { bg: "#F0F0F0", color: "#666666" };
    }
  };

  const filteredSales = sales?.filter((sale) => {
    const statusMatch =
      selectedStatuses.length === 0 || selectedStatuses.includes(sale.status);
    return statusMatch;
  });

  // Table columns configuration
  const columns = [
    {
      title: t("marketplace.table.sale_id"),
      field: "salesNumber",
      sortable: true,
    },
    {
      title: t("product_form.name"),
      field: "productName",
      sortable: true,
    },
    {
      title: t("marketplace.table.date"),
      field: "createdAt",
      sortable: true,
      render: (row: Sale) => (
        <span className="text-gray-600">
          {format(new Date(row.createdAt), "PPP", {
            locale: locale === "ar" ? arSA : enUS,
          })}
        </span>
      ),
    },
    {
      title: t("marketplace.table.status"),
      field: "status",
      sortable: true,
      render: (row: Sale) => (
        <Chip
          label={t(`orders.status.${row.status.toLowerCase()}`)}
          sx={{
            backgroundColor: getStatusColor(row.status).bg,
            color: getStatusColor(row.status).color,
            fontWeight: 500,
            fontSize: "0.75rem",
            minWidth: "100px",
            borderRadius: 1,
          }}
        />
      ),
    },
    {
      title: t("marketplace.table.price"),
      field: "price",
      sortable: true,
      render: (row: Sale) => (
        <span className="font-medium text-gray-900">
          {new Intl.NumberFormat(locale, {
            style: "currency",
            currency: "SAR",
          }).format(row.price)}
        </span>
      ),
    },
    {
      title: t("profile.fields.address"),
      field: "customerAddress",
      sortable: true,
      hideOnMobile: true,
      render: (row: Sale) => (
        <span className="text-gray-600 text-sm">{row.customerAddress}</span>
      ),
    },
    {
      title: t("orders.table.quantity"),
      field: "quantity",
      sortable: true,
      hideOnMobile: true,
      render: (row: Sale) => (
        <span className="font-medium text-gray-900">{row.quantity}</span>
      ),
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: {
          xs: "12px 16px",
          sm: "16px 20px",
          md: "20px 24px",
          lg: "24px 32px",
        },
        direction: "ltr",
        overflowX: "hidden",
      }}
    >
      <Container maxWidth={false} sx={{ p: 0 }}>
        {/* Header Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Download size={16} />}
            sx={{
              borderColor: "#d1d5db",
              color: "#6b7280",
              "&:hover": {
                borderColor: "#644980",
                color: "#644980",
              },
            }}
          >
            {t("supplier_pages.product_list.export")}
          </Button>

          <Button
            variant="outlined"
            startIcon={<Filter size={16} />}
            endIcon={<ArrowDown size={16} />}
            onClick={handleClick}
            sx={{
              borderColor: "#d1d5db",
              color: "#6b7280",
              "&:hover": {
                borderColor: "#644980",
                color: "#644980",
              },
            }}
          >
            {t("buyer_pages.products_list.filter")}
            {selectedStatuses.length > 0 && (
              <Box
                sx={{
                  ml: 1,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#644980",
                }}
              />
            )}
          </Button>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                width: 320,
                borderRadius: 2,
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                border: "1px solid #e5e7eb",
              },
            }}
          >
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#374151", mb: 1 }}
              >
                {t("buyer_pages.offers_list.status")}
              </Typography>
              {allStatuses.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      checked={selectedStatuses.includes(status)}
                      onChange={() => handleStatusToggle(status)}
                      size="small"
                      sx={{
                        color: "#6b7280",
                        "&.Mui-checked": {
                          color: "#644980",
                        },
                      }}
                    />
                  }
                  label={t(`orders.status.${status.toLowerCase()}`)}
                  sx={{
                    display: "block",
                    margin: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      color: "#374151",
                    },
                  }}
                />
              ))}
            </Box>

            <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "#374151", mb: 2 }}
              >
                {t("marketplace.table.date")}
              </Typography>
              <DatePicker mode="single" className="w-full" />
            </Box>

            <Box sx={{ p: 2, display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                size="small"
                sx={{
                  flex: 1,
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                Clear
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                size="small"
                sx={{
                  flex: 1,
                  background:
                    "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
                  color: "white",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #644980 0%, #1e40af 100%)",
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Menu>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "none",
                background: "white",
                border: "1px solid #e2e8f0",
                height: "100%",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#6b7280", mb: 1, fontSize: "0.875rem" }}
                >
                  {t("marketplace.total_sales")}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#1f2937", mb: 1 }}
                >
                  {sales?.length}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ChevronUp size={16} color="#10b981" />
                  <Typography
                    variant="body2"
                    sx={{ color: "#10b981", fontSize: "0.75rem" }}
                  >
                    25.2% {t("marketplace.last_week")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "none",
                background: "white",
                border: "1px solid #e2e8f0",
                height: "100%",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#6b7280", mb: 1, fontSize: "0.875rem" }}
                >
                  {t("marketplace.returns")}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#1f2937", mb: 1 }}
                >
                  {sales.filter((sale) => sale.status === "CANCELLED").length}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ChevronUp size={16} color="#10b981" />
                  <Typography
                    variant="body2"
                    sx={{ color: "#10b981", fontSize: "0.75rem" }}
                  >
                    25.2% {t("marketplace.last_week")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "none",
                background: "white",
                border: "1px solid #e2e8f0",
                height: "100%",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#6b7280", mb: 1, fontSize: "0.875rem" }}
                >
                  {t("marketplace.active_sales")}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#1f2937", mb: 1 }}
                >
                  {
                    sales.filter((sale) => sale.status === STATUS_ORDER.PENDING)
                      .length
                  }
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ChevronUp size={16} color="#10b981" />
                  <Typography
                    variant="body2"
                    sx={{ color: "#10b981", fontSize: "0.75rem" }}
                  >
                    25.2% {t("marketplace.last_week")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                borderRadius: 2,
                boxShadow: "none",
                background: "white",
                border: "1px solid #e2e8f0",
                height: "100%",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="h6"
                  sx={{ color: "#6b7280", mb: 1, fontSize: "0.875rem" }}
                >
                  {t("marketplace.earnings")}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#1f2937", mb: 1 }}
                >
                  {new Intl.NumberFormat(locale, {
                    style: "currency",
                    currency: "SAR",
                  }).format(sales.reduce((sum, sale) => sum + sale.price, 0))}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ChevronUp size={16} color="#10b981" />
                  <Typography
                    variant="body2"
                    sx={{ color: "#10b981", fontSize: "0.75rem" }}
                  >
                    25.2% {t("marketplace.last_week")}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Modern Table */}
        <TableComponent
          columns={columns}
          data={filteredSales}
          rowKeyField="id"
          onRowClick={handleRowClick}
          maxHeight={600}
          pagination={true}
          searchable={true}
          sortable={true}
          title={t("marketplace.sales_table_title")}
          subtitle={t("marketplace.sales_table_subtitle")}
          emptyStateMessage={t("marketplace.no_sales_found")}
          emptyStateSubmessage={t("marketplace.no_sales_matching_criteria")}
          variant="elevated"
          size="md"
        />

        {/* Order Details Dialog */}
        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
              borderBottom: "1px solid #e5e7eb",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937" }}>
              {t("orders.title")} - {selectedOrder?.orderId}
            </Typography>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            {selectedOrder && (
              <Box sx={{ mt: 1 }}>
                <Box sx={{ display: "flex", gap: 3, mb: 3 }}>
                  <Avatar
                    src={selectedOrder.productImage}
                    alt={selectedOrder.productName}
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 2,
                      border: "1px solid #e5e7eb",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#1f2937", mb: 1 }}
                    >
                      {selectedOrder.productName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#6b7280", mb: 0.5 }}
                    >
                      {t("orders.table.quantity")}: {selectedOrder.quantity}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {t("marketplace.table.price")}:{" "}
                      {new Intl.NumberFormat(locale, {
                        style: "currency",
                        currency: "SAR",
                      }).format(selectedOrder.price)}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#1f2937", mb: 2 }}
                >
                  {t("orders.customerInfo")}
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" sx={{ color: "#374151", mb: 1 }}>
                    {t("profile.fields.nickName")}: {selectedOrder.customerName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#374151" }}>
                    {t("profile.fields.address")}:{" "}
                    {selectedOrder.customerAddress}
                  </Typography>
                </Box>

                {selectedOrder.notes && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#1f2937", mb: 2 }}
                    >
                      {t("orders.notes")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#6b7280", pl: 2 }}
                    >
                      {selectedOrder.notes}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              p: 3,
              background: "linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)",
              borderTop: "1px solid #e5e7eb",
              gap: 2,
            }}
          >
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{
                borderColor: "#d1d5db",
                color: "#6b7280",
                "&:hover": {
                  borderColor: "#9ca3af",
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              {t("actions.close")}
            </Button>
            {selectedOrder?.status === STATUS_ORDER.PENDING && (
              <>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleUpdateStatus(STATUS_ORDER.REJECTED)}
                  disabled={isUpdating}
                  sx={{
                    borderColor: "#ef4444",
                    color: "#ef4444",
                    "&:hover": {
                      borderColor: "#dc2626",
                      backgroundColor: "#fef2f2",
                    },
                  }}
                >
                  {t("actions.reject")}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleUpdateStatus(STATUS_ORDER.ACCEPTED)}
                  disabled={isUpdating}
                  sx={{
                    background:
                      "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
                    color: "white",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #644980 0%, #1e40af 100%)",
                    },
                    "&:disabled": {
                      background: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  {t("actions.accept")}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default SellerSales;
